import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
  getDomainContext,
  isLocalEnvironment,
} from '@pbt/pbt-ui-components/src/utils'
import { Environment } from '@pbt/pbt-ui-components/src/constants/environment'
import { getCurrentBusiness, getCurrentUser } from '@pbt/pbt-portal-ui/src/store/reducers/auth'
import { getRolesMap } from '@pbt/pbt-portal-ui/src/store/reducers/roles'
import useIsAuthenticated from '@pbt/pbt-portal-ui/src/utils/useIsAuthenticated'

import { ENVIRONMENT_VARIABLES } from '../../constants/environmentVariables'
import { initStorefrontAnalytics } from '../utils/storefrontAnalytics'

const getDevEnvironment = () => {
  // Get dev environment from localStorage, defaulting to STAGE
  const devEnv = localStorage.getItem('devEnv') as Environment
  return devEnv || Environment.STAGE
}

const StorefrontAnalyticsWidget = () => {
  const user = useSelector(getCurrentUser)
  const business = useSelector(getCurrentBusiness)
  const roles = useSelector(getRolesMap)
  const userRoles = useMemo(
    () => user?.businessToRoleList
      ?.filter(role => role.business === business?.id)
      .map(role => roles[role.role]?.name) || undefined,
    [user, business, roles],
  )

  const { isAuthenticated } = useIsAuthenticated()

  const domainContext = getDomainContext()
  const env = isLocalEnvironment() ? getDevEnvironment() : domainContext.env
  const envVars = ENVIRONMENT_VARIABLES[env] || {}
  const { SEGMENT_ENV_ID } = envVars
  const { GTM_CONTAINER_ID } = envVars
  const GTM_EXTRA_PARAMS = envVars.GTM_EXTRA_PARAMS || ''

  useEffect(() => {
    // Inject Segment
    let segmentScript: HTMLScriptElement | undefined
    if (SEGMENT_ENV_ID && !document.getElementById('segment-script')) {
      segmentScript = document.createElement('script')
      segmentScript.id = 'segment-script'
      // Segment.io analytics script - minified
      // eslint-disable-next-line max-len
      const segmentSnippet = `!function(){var analytics=window.analytics=window.analytics||[];if(!analytics.initialize)if(analytics.invoked)window.console&&console.error&&console.error("Segment snippet included twice.");else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware"];analytics.factory=function(e){return function(){var t=Array.prototype.slice.call(arguments);t.unshift(e);analytics.push(t);return analytics}};for(var e=0;e<analytics.methods.length;e++){var key=analytics.methods[e];analytics[key]=analytics.factory(key)}analytics.load=function(key,e){var t=document.createElement("script");t.type="text/javascript";t.async=!0;t.src="https://cdn.segment.com/analytics.js/v1/" + key + "/analytics.min.js";var n=document.getElementsByTagName("script")[0];n.parentNode.insertBefore(t,n);analytics._loadOptions=e};analytics.SNIPPET_VERSION="4.13.1";analytics.load('${SEGMENT_ENV_ID}');}}();`
      segmentScript.innerHTML = `
        try {
          ${segmentSnippet}
        } catch(error) {
          console.error('Error caught in segment', error);
        }
      `
      if (process.env.NODE_ENV !== 'test') {
        document.head.appendChild(segmentScript)
      }
    }

    // Inject GTM
    let gtmScript: HTMLScriptElement | undefined
    if (GTM_CONTAINER_ID && !document.getElementById('gtm-script')) {
      gtmScript = document.createElement('script')
      gtmScript.id = 'gtm-script'
      gtmScript.innerHTML = `
        window.chewy = window.chewy || {};
        window.chewy.analyticsCallback = () => {
          // [Start] Initialize Datalayer
          try {
            window.chewy.initializeDataLayer && window.chewy.initializeDataLayer(true);
          } catch (error) {
            console.error('Error caught in gtm', error);
          }
          // [End] Initialize Datalayer
          // [Start] Google Tag Manager
          try {
            (function(w, d, s, l, i) {
              w[l] = w[l] || [];
              w[l].push({
                "gtm.start": new Date().getTime(),
                event: "gtm.js"
              });
              var f = d.getElementsByTagName(s)[0],
                j = d.createElement(s),
                dl = l != "dataLayer" ? "&l=" + l : "";
              j.async = true;
              j.src =
                "https://www.googletagmanager.com/gtm.js?id=" +
                i +
                dl +
                '${GTM_EXTRA_PARAMS}';
              f.parentNode.insertBefore(j, f);
            })(window, document, "script", "dataLayer", '${GTM_CONTAINER_ID}');
          } catch (error) {
            console.error('Error caught in gtm tag', error);
          }
          // [End] Google Tag Manager
        }
      `
      document.head.appendChild(gtmScript)
    }

    // Inject GTM <noscript> fallback
    let gtmNoscript: HTMLElement | undefined
    if (GTM_CONTAINER_ID && !document.getElementById('gtm-noscript')) {
      gtmNoscript = document.createElement('noscript')
      gtmNoscript.id = 'gtm-noscript'
      gtmNoscript.innerHTML = `
        <iframe
          title="gtm-noscript"
          src="https://www.googletagmanager.com/ns.html?id=${GTM_CONTAINER_ID}${GTM_EXTRA_PARAMS}"
          height="0"
          width="0"
          style="display:none;visibility:hidden"
        ></iframe>
      `
      document.body.appendChild(gtmNoscript)
    }
  }, [SEGMENT_ENV_ID, GTM_CONTAINER_ID, GTM_EXTRA_PARAMS])

  useEffect(() => {
    if (isAuthenticated && user && SEGMENT_ENV_ID) {
      // Update storefront analytics with user context when authenticated
      initStorefrontAnalytics({
        userData: user,
        business,
        userRoles,
      })
    }
  }, [user, isAuthenticated, business, userRoles, SEGMENT_ENV_ID])

  return null
}

export default StorefrontAnalyticsWidget

